import { useState } from 'react';
/**
 * A company's mailboxes for voicemail emails (1.12.27): moved from the phone system's own emails to
 * Stitch's, one at a time, and put back. Since 1.12.33 the companies list and moving many at once
 * live in Stitch Admin → Updates → Voicemail emails; this drawer is its Details.
 */
import {
  voicemailMoveApi as vm,
  type VoicemailMoveCompany,
  type VoicemailMoveCompanyResult,
  type VoicemailMoveMailbox,
  type VoicemailMoveOutcome,
  type VoicemailMoveResult,
  type VoicemailMoveRun,
  type VoicemailMoveScope
} from '../../api/client';
import { Button, Chip, Drawer, Empty, LoadError, Loading, Notice } from '../../components/ui';
import { shortWhen, type Tone } from '../../lib/format';
import { useData } from '../../lib/useData';

type Action = 'move' | 'putBack';

const OUTCOME: Record<VoicemailMoveOutcome, { label: string; tone: Tone }> = {
  moved: { label: 'Moved', tone: 'ok' },
  already: { label: 'Already moved', tone: 'neutral' },
  put_back: { label: 'Put back', tone: 'ok' },
  nothing: { label: 'Nothing to put back', tone: 'neutral' },
  gone: { label: 'No longer there', tone: 'neutral' },
  failed: { label: 'Failed', tone: 'crit' }
};

const STATE: Record<VoicemailMoveMailbox['state'], { label: string; tone: Tone }> = {
  on: { label: 'Phone system emails on', tone: 'warn' },
  moved: { label: 'Moved', tone: 'ok' },
  off: { label: 'Off, nothing saved', tone: 'neutral' },
  changed: { label: 'Moved, some on again', tone: 'warn' },
  unknown: { label: 'Couldn’t read', tone: 'crit' }
};

function resultDetail(m: VoicemailMoveResult, action: Action): string {
  const emails = m.emails?.length ? `${action === 'move' ? 'Had' : 'On again'}: ${m.emails.join(', ')}.` : '';
  return [emails, m.detail ?? ''].filter(Boolean).join(' ') || '—';
}


/** One company's mailboxes as the phone system has them now: move or put back one at a time (also Updates → Voicemail emails → Details). */
export function VoicemailMailboxes({
  company,
  canChange,
  busy,
  go,
  onClose
}: {
  company: VoicemailMoveCompany;
  canChange: boolean;
  busy: string;
  go: (action: Action, scope: VoicemailMoveScope) => Promise<VoicemailMoveRun | null>;
  onClose: () => void;
}) {
  const list = useData(() => vm.mailboxes(company.customerId), [company.customerId], { live: false });
  const [last, setLast] = useState<{ result: VoicemailMoveResult; stitch: VoicemailMoveCompanyResult['stitchEmails']; action: Action } | null>(null);

  const act = async (action: Action, mailbox: string) => {
    const r = await go(action, { customer: company.customerId, mailbox });
    const c = r?.companies[0];
    const result = c?.mailboxes.find((m) => m.extension === mailbox);
    if (c && result) setLast({ result, stitch: c.stitchEmails, action });
    list.reload();
  };

  return (
    <Drawer open onClose={onClose} title={`Mailboxes at ${company.name}`} subtitle={company.domain}>
      <div className="section-stack">
        {!company.writable && <Notice>Stitch can’t change this tenant right now: what each mailbox has is shown, and nothing here can change.</Notice>}
        {last && (
          <Notice tone={last.result.outcome === 'failed' ? 'crit' : 'ok'} title={`${last.result.extension}: ${OUTCOME[last.result.outcome].label.toLowerCase()}`}>
            {[resultDetail(last.result, last.action).replace(/^—$/, ''), last.stitch.changed ? `Stitch’s voicemail emails: ${last.stitch.note.toLowerCase()}.` : ''].filter(Boolean).join(' ')}
          </Notice>
        )}
        {list.error ? (
          <LoadError error={list.error} retry={list.reload} />
        ) : !list.data ? (
          <Loading label="Reading the mailboxes from the phone system" />
        ) : list.data.mailboxes.length === 0 ? (
          <Empty title="No mailboxes">This company has no extensions on the phone system.</Empty>
        ) : (
          <ul className="vm-mailbox-list">
            {list.data.mailboxes.map((m) => (
              <li key={m.extension}>
                <span className="cell-stack grow">
                  <span>
                    <strong style={{ fontWeight: 650 }}>{m.extension}</strong> {m.name}
                    {m.box && <span className="muted"> · Voicemail box</span>}
                  </span>
                  <small>{stateDetail(m)}</small>
                </span>
                <Chip small tone={STATE[m.state].tone}>
                  {STATE[m.state].label}
                </Chip>
                {canChange && (
                  <span className="row-actions">
                    {m.saved && (
                      <Button size="small" variant="ghost" disabled={Boolean(busy)} onClick={() => void act('putBack', m.extension)} aria-label={`Put back ${m.extension}`}>
                        Put back
                      </Button>
                    )}
                    {(m.state === 'on' || m.state === 'changed') && (
                      <Button size="small" disabled={Boolean(busy)} onClick={() => void act('move', m.extension)} aria-label={`Move ${m.extension} to Stitch`}>
                        Move
                      </Button>
                    )}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Drawer>
  );
}

function stateDetail(m: VoicemailMoveMailbox): string {
  const had = m.had?.length ? `Had: ${m.had.join(', ')}.` : m.saved ? 'Had none of them on.' : '';
  switch (m.state) {
    case 'on':
      return `Sends: ${m.on.join(', ') || 'its own emails'}.`;
    case 'moved':
      return `${had}${m.savedAt ? ` Saved ${shortWhen(m.savedAt)}.` : ''}`;
    case 'changed':
      return `On again: ${m.on.join(', ') || 'some of its own emails'}. ${had}`;
    case 'off':
      return 'None of the phone system’s own emails are on.';
    default:
      return m.detail ?? '';
  }
}
